import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { Quapp } from '../types/quapp'

/**
 * Fetch quapp details by slug
 * Used by Web Frame to load quapp data
 */
async function fetchQuappBySlug(slug: string): Promise<Quapp> {
  const { data, error } = await supabase
    .rpc('get_quapp_by_slug', { p_slug: slug })
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      throw new Error('NOT_FOUND')
    }
    throw new Error(error.message)
  }

  if (!data) {
    throw new Error('NOT_FOUND')
  }

  // RPC returns { success: boolean, quapp: Quapp } structure
  const response = data as { success: boolean; quapp: Quapp | null }
  
  if (!response.success || !response.quapp) {
    throw new Error('NOT_FOUND')
  }

  return response.quapp
}

/**
 * React Query hook to fetch quapp by slug
 * 
 * @param slug - The quapp slug from URL
 * @returns Query result with quapp data, loading state, and error
 * 
 * @example
 * ```tsx
 * const { data: quapp, isLoading, error } = useQuappBySlug('hello-world')
 * ```
 */
export function useQuappBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['quapp', 'slug', slug],
    queryFn: () => fetchQuappBySlug(slug!),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,   // 30 minutes cache
    retry: (failureCount, error) => {
      // Don't retry on NOT_FOUND
      if (error instanceof Error && error.message === 'NOT_FOUND') {
        return false
      }
      return failureCount < 2
    },
  })
}

